/* EM home hero rotation.
   Uses the theme's own Swiper bundle (loaded in layout/theme.liquid) -- no
   second slider library. */
(function () {
  'use strict';

  var READY_CLASS = 'is-ready';
  var PAUSED_CLASS = 'is-paused';
  var STATIC_CLASS = 'is-static';
  var CURRENT_CLASS = 'is-current';

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function prefersReducedMotion() {
    return (
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function setCounter(root, index, total) {
    var counter = root.querySelector('[data-em-hero-counter]');
    if (counter) {
      counter.textContent = pad(index + 1) + ' / ' + pad(total);
    }
  }

  function slidesOf(root) {
    return root.querySelectorAll('.swiper-wrapper > .swiper-slide');
  }

  /* Mark which slide is on screen ourselves. The tiles are links and the fade
     effect stacks all three slides in one box, so something has to say which
     stack may take a click.

     Read the slides from the DOM, not from `swiper.slides`. The bundle this
     theme ships keeps that array EMPTY (measured 2026-09-12: 3 slides in the
     wrapper, `swiper.slides.length === 0`, unchanged by `update()`), which is
     also why it never stamps `swiper-slide-active` on anything -- it has no
     list to walk. `activeIndex` is maintained correctly, so index + DOM order
     is the pair that can be trusted here. */
  function markCurrent(root, index) {
    var slides = slidesOf(root);
    for (var i = 0; i < slides.length; i++) {
      slides[i].classList.toggle(CURRENT_CLASS, i === index);
    }
  }

  /* A grid cell holding several products fades through them on its own clock.
     Each cell is offset by a share of the interval, so the cells never all turn
     at the same moment -- the grid keeps moving without flickering as a block. */
  function startCells(root, paused) {
    var cells = root.querySelectorAll('[data-em-hero-cell]');
    var timers = [];

    for (var i = 0; i < cells.length; i++) {
      (function (cell) {
        var frames = cell.querySelectorAll('.em-hero-items__frame');
        if (frames.length < 2) return;

        var interval = parseInt(cell.dataset.interval, 10);
        if (isNaN(interval) || interval < 1000) interval = 4000;
        var offset = parseInt(cell.dataset.offset, 10);
        if (isNaN(offset) || offset < 0) offset = 0;

        /* Each frame is a link. Only the visible one belongs in the tab order;
           the hidden ones stay out of it so keyboard users are not walked
           through products they cannot see. */
        function reach(frame, on) {
          if (on) {
            frame.removeAttribute('tabindex');
            frame.removeAttribute('aria-hidden');
          } else {
            frame.setAttribute('tabindex', '-1');
            frame.setAttribute('aria-hidden', 'true');
          }
        }

        var at = 0;
        function advance() {
          if (paused() || document.hidden) return;
          frames[at].classList.remove('is-on');
          reach(frames[at], false);
          at = (at + 1) % frames.length;
          frames[at].classList.add('is-on');
          reach(frames[at], true);
        }
        timers.push(
          setTimeout(function () {
            advance();
            timers.push(setInterval(advance, interval));
          }, offset)
        );
      })(cells[i]);
    }
    return timers;
  }

  function init(root) {
    if (!root || root.dataset.emHeroInit === '1') return;

    var slides = slidesOf(root);
    var total = slides.length;

    var still = prefersReducedMotion();
    function isPaused() {
      return root.classList.contains(PAUSED_CLASS);
    }

    // One slide is not a slideshow -- but its cells can still cycle. The lone
    // slide is still marked current: READY_CLASS closes every tile to clicks,
    // and without the mark nothing would reopen them.
    if (total < 2) {
      if (slides[0]) slides[0].classList.add(CURRENT_CLASS);
      root.classList.add(READY_CLASS, STATIC_CLASS);
      root.dataset.emHeroInit = '1';
      if (!still) startCells(root, isPaused);
      return;
    }

    var duration = parseInt(root.dataset.duration, 10);
    if (isNaN(duration) || duration < 1000) duration = 7000;
    root.style.setProperty('--em-hero-duration', duration + 'ms');

    var pagination = root.querySelector('[data-em-hero-pagination]');

    var options = {
      effect: 'fade',
      fadeEffect: { crossFade: true },
      speed: still ? 0 : 700,
      // rewind, not loop: loop clones the slides, and a cloned slide would put
      // a second <h1> with the same id into the page. rewind goes back to the
      // first slide after the last one without duplicating anything.
      loop: false,
      rewind: true,
      slidesPerView: 1,
      autoHeight: false,
      watchSlidesProgress: true,
      a11y: { enabled: true },
      keyboard: { enabled: true, onlyInViewport: true },
      autoplay: still
        ? false
        : {
            delay: duration,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          },
      on: {
        init: function () {
          root.classList.add(READY_CLASS);
          if (still) root.classList.add(STATIC_CLASS);
          setCounter(root, this.realIndex, total);
          markCurrent(root, this.activeIndex);
        },
        slideChange: function () {
          setCounter(root, this.realIndex, total);
          markCurrent(root, this.activeIndex);
        },
      },
    };

    if (pagination) {
      options.pagination = { el: pagination, clickable: true };
    }

    var swiper = new window.Swiper(root, options);
    root.dataset.emHeroInit = '1';

    if (!still) startCells(root, isPaused);

    if (!still) {
      // The progress bar is a CSS animation, so it needs telling when the
      // autoplay it represents is paused.
      root.addEventListener('mouseenter', function () {
        root.classList.add(PAUSED_CLASS);
      });
      root.addEventListener('mouseleave', function () {
        root.classList.remove(PAUSED_CLASS);
      });
      root.addEventListener('focusin', function () {
        root.classList.add(PAUSED_CLASS);
        if (swiper.autoplay) swiper.autoplay.pause();
      });
      root.addEventListener('focusout', function () {
        root.classList.remove(PAUSED_CLASS);
        if (swiper.autoplay) swiper.autoplay.resume();
      });
    }
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-em-hero]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  // Swiper is a deferred script in the head, so it is normally defined by the
  // time this runs. In the theme editor a section can re-render before that, so
  // wait a moment rather than failing silently.
  function whenSwiperReady(fn) {
    if (window.Swiper) return fn();
    var tries = 0;
    var timer = setInterval(function () {
      if (window.Swiper || ++tries > 40) {
        clearInterval(timer);
        if (window.Swiper) fn();
      }
    }, 50);
  }

  function boot() {
    whenSwiperReady(function () {
      initAll(document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-init when the section is reloaded.
  document.addEventListener('shopify:section:load', function (event) {
    whenSwiperReady(function () {
      initAll(event.target);
    });
  });
})();
